import { tv, VariantProps } from "@utils/tw"
import { useRoute } from "eddev/routing"
import { LayoutGroup, motion } from "motion/react"
import { ReactNode, useEffect, useId } from "react"

type Props = {
  layoutKey?: string | null
  eyebrow?: ReactNode
  title?: ReactNode
  description?: ReactNode
} & VariantProps<typeof createStyles>

/**
 * Used for most page headers, and little page sections.
 *
 * Pass in a `layoutKey` if you'd like to use layout transitions.
 */
export function PageIntro(props: Props) {
  const id = useId()
  const styles = createStyles(props)

  return (
    <LayoutGroup id={props.layoutKey ?? id} inherit>
      <div className={styles.base()}>
        <div className={styles.container()}>
          {props.eyebrow && (
            <motion.div
              layoutId="eyebrow"
              variants={{
                hide: { opacity: 0, filter: "blur(10px)" },
                show: { opacity: 1, filter: "none" },
              }}
              initial="hide"
              animate="show"
              exit="hide"
              className={styles.eyebrow()}
            >
              {props.eyebrow}
            </motion.div>
          )}
          {props.title && (
            <motion.div
              layoutId="title"
              variants={{
                hide: { opacity: 0, filter: "blur(20px)" },
                show: { opacity: 1, filter: "none" },
              }}
              initial="hide"
              animate="show"
              exit="hide"
              className={styles.title()}
            >
              {props.title}
            </motion.div>
          )}
          {props.description && (
            <motion.div
              layoutId="description"
              initial={{ opacity: 0, filter: "blur(20px)" }}
              animate={{ opacity: 1, filter: "none" }}
              exit={{ opacity: 0, filter: "blur(20px)" }}
              className={styles.description()}
            >
              {props.description}
            </motion.div>
          )}
        </div>
      </div>
    </LayoutGroup>
  )
}

const createStyles = tv({
  base: "w-full text-balance",
  slots: {
    container: "mx-auto flex flex-col text-center items-center justify-center",
    eyebrow: "type-label-l flex items-center justify-center",
    title: "type-title-l mt-6 max-w-[12em]",
    description: "type-text-s text-white/60 mt-9  max-w-[40em]",
  },
  variants: {
    spacingTop: {
      page: {
        base: "pt-12",
      },
      full: {
        base: "h-[100vh] flex items-center justify-center",
      },
    },
  },
})
